import { nextTick } from 'vue'

import type { Ref } from 'vue'

// Article-slider-specific cell trap: handles embed consent flows, SDK-injected iframes
// and regular content with text links. Uses a dedicated composable so the flickity-slider's
// keepTabbingFocusInSlider remains intact for other slider types (hot-gossip, teaser, etc.).
export const useArticleSliderFocusTrap = (
  flickityDiv: Ref<HTMLElement | null>,
  options: { triggerResize: () => void }
) => {
  const keepTabbingFocusInArticleSlider = (): void => {
    const selectedCell = flickityDiv.value?.querySelector<HTMLElement>('.bx-slider__cell.is-selected')
    if (!selectedCell) return

    // Creates a visible "Embed öffnen" link and injects it before the given anchor element.
    // Necessary because iframes swallow key events, so we can't trap focus inside them directly.
    const makeEmbedLink = (embedUrl: string, anchorBefore: Element): HTMLAnchorElement => {
      const el = document.createElement('a')
      el.className = 'bx-slider__embed-link bx-typo--link bx-typo--bold'
      el.href = embedUrl
      el.target = '_blank'
      el.rel = 'noopener noreferrer'
      el.textContent = 'Embed öffnen'
      el.style.cssText = 'display:block;width:fit-content;margin:24px auto;padding:10px 16px'
      anchorBefore.parentNode?.insertBefore(el, anchorBefore)
      return el
    }

    // Returns the focusable elements for the current cell state:
    //  - consent already given (revokeBtn present): synthetic embed link + revoke button
    //  - consent pending (consentBtn present): description link + consent button
    //  - regular content: all links and buttons, excluding player controls
    const buildItems = (capturedEmbedUrl?: string): HTMLElement[] => {
      const revokeBtn = selectedCell.querySelector<HTMLElement>('.bx-placeholder__revoke-consent')
      if (revokeBtn) {
        revokeBtn.setAttribute('tabindex', '0')
        let embedLink = selectedCell.querySelector<HTMLAnchorElement>('.bx-slider__embed-link')
        if (!embedLink) {
          // Resolve the embed URL from multiple sources; fall back to the cell-level cache
          // so re-entry after Escape still works even if the original DOM element is gone.
          const iframe = selectedCell.querySelector<HTMLIFrameElement>('iframe')
          const pinEl = selectedCell.querySelector<HTMLElement>('[data-pin-do][data-pin-href]')
          const contentLink = selectedCell.querySelector<HTMLAnchorElement>('a[href]:not(.bx-placeholder__revoke-consent)')
          const embedUrl = capturedEmbedUrl ?? selectedCell.dataset.bxCachedEmbedUrl ?? iframe?.src ?? pinEl?.dataset.pinHref ?? contentLink?.href
          if (embedUrl) {
            selectedCell.dataset.bxCachedEmbedUrl = embedUrl
            // Prefer the embed content wrapper so the link appears above the embed, not below it.
            const contentDiv = selectedCell.querySelector('[class*="bx-content--"]')
            const insertBefore = contentDiv ?? iframe?.parentNode as Element ?? contentLink ?? revokeBtn
            embedLink = makeEmbedLink(embedUrl, insertBefore)
            nextTick(() => {
              options.triggerResize()
            })
          }
        }
        return [embedLink, revokeBtn].filter((el): el is HTMLElement => !!el)
      }
      const consentBtn = selectedCell.querySelector<HTMLElement>('.bx-placeholder__button')
      if (consentBtn) {
        const textLink = selectedCell.querySelector<HTMLElement>('.bx-placeholder__description a')
        if (textLink) textLink.setAttribute('tabindex', '0')
        consentBtn.setAttribute('tabindex', '0')
        return [textLink, consentBtn].filter((el): el is HTMLElement => !!el)
      }
      // Regular content — exclude JW player DOM buttons which appear before content links
      const regularItems = Array.from(selectedCell.querySelectorAll<HTMLElement>('a[href], button'))
        .filter(el => !el.closest('.jwplayer, .bdu-widget, .bx-content__media-video-wrapper'))
      regularItems.forEach(el => el.setAttribute('tabindex', '0'))
      return regularItems
    }

    let items = buildItems()

    // Cycle focus forward/backward through the current item list, wrapping around.
    const cycleNext = (): void => {
      const idx = items.indexOf(document.activeElement as HTMLElement)
      items[idx >= items.length - 1 ? 0 : idx + 1]?.focus()
    }
    const cyclePrev = (): void => {
      const idx = items.indexOf(document.activeElement as HTMLElement)
      items[idx <= 0 ? items.length - 1 : idx - 1]?.focus()
    }

    // Intercepts keyboard events while focus is trapped inside the cell.
    // Tab/arrows cycle through items; Escape releases the trap.
    const cellKeyHandler = (e: KeyboardEvent): void => {
      if (!['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(e.key)) return
      e.stopPropagation()
      if (e.key === 'Escape') { exitCell(); return }
      if (e.key === 'Tab') { e.preventDefault(); e.shiftKey ? cyclePrev() : cycleNext(); return }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); cycleNext(); return }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); cyclePrev(); return }
      // a / button elements handle Enter natively
    }

    selectedCell.addEventListener('keydown', cellKeyHandler)

    let consentObserver: MutationObserver | null = null
    let revokeObserver: MutationObserver | null = null

    // Watches for the consent placeholder to reappear (i.e. user revoked consent),
    // then releases the focus trap so the slider regains control.
    const startRevokeObserver = (): void => {
      revokeObserver = new MutationObserver(() => {
        if (!selectedCell.querySelector('.bx-placeholder__button')) return
        revokeObserver?.disconnect()
        revokeObserver = null
        exitCell()
      })
      revokeObserver.observe(selectedCell, { childList: true, subtree: true })
    }

    if (selectedCell.querySelector('.bx-placeholder__revoke-consent')) {
      startRevokeObserver()
    } else if (selectedCell.querySelector('.bx-placeholder__button')) {
      consentObserver = new MutationObserver(() => {
        if (selectedCell.querySelector('.bx-placeholder__button')) return
        consentObserver?.disconnect()
        consentObserver = null
        items.forEach(el => el.setAttribute('tabindex', '-1'))
        nextTick(() => {
          const capturedEmbedUrl = (() => {
            const dataHrefEl = selectedCell.querySelector<HTMLElement>('[data-href]')
            if (dataHrefEl?.dataset.href) return dataHrefEl.dataset.href
            const pinLink = selectedCell.querySelector<HTMLAnchorElement>('a[data-pin-do][href]')
            if (pinLink) return pinLink.href
            const twitterLinks = selectedCell.querySelectorAll<HTMLAnchorElement>('.twitter-tweet a[href]')
            if (twitterLinks.length) return twitterLinks[twitterLinks.length - 1].href
            const iframe = selectedCell.querySelector<HTMLIFrameElement>('iframe')
            if (iframe?.src) return iframe.src
            return undefined
          })()
          setTimeout(() => {
            nextTick(() => {
              items = buildItems(capturedEmbedUrl)
              options.triggerResize()
              const iframe = selectedCell.querySelector('iframe')
              if (iframe) {
                iframe.addEventListener('load', () => {
                  setTimeout(() => {
                    options.triggerResize()
                  }, 100) // wait a tick for potential SDK-rendered buttons to appear
                })
              }
              items[0]?.focus()
              startRevokeObserver()
            })
          }, 800)
        })
      })
      consentObserver.observe(selectedCell, { childList: true, subtree: true })
    }

    // Tears down the focus trap: removes the key listener, disconnects observers,
    // resets tabindices, removes the synthetic embed link, and returns focus to the slider.
    const exitCell = (): void => {
      selectedCell.removeEventListener('keydown', cellKeyHandler)
      consentObserver?.disconnect()
      revokeObserver?.disconnect()
      items.forEach(el => el.setAttribute('tabindex', '-1'))
      selectedCell.querySelector('.bx-slider__embed-link')?.remove()
      flickityDiv.value?.focus()
      nextTick(() => options.triggerResize())
    }

    setTimeout(() => { items[0]?.focus() }, 100)
  }

  return { keepTabbingFocusInArticleSlider }
}
